/**
 * Single source of truth for navigation: the desktop top row, the section row,
 * the "▾" quick-jump, the phone menu, page tabs and module gating all read this.
 *
 * Pure data + pure functions (no React) so it is trivially unit-testable.
 */

export type ModuleId =
  | 'shipping'
  | 'rma'
  | 'claims'
  | 'replenishment'
  | 'procurement'
  | 'products'
  | 'orders'
  | 'warehousing'
  | 'receiving';

export type GroupId = 'dashboard' | 'operations' | 'shipping' | 'returns' | 'admin';

export interface NavTab {
  path: string;
  labelKey: string;
  /** Exact match only (otherwise prefix match on a segment boundary). */
  end?: boolean;
}

export interface NavSection {
  id: string;
  groupId: GroupId;
  labelKey: string;
  /** Where clicking the section goes. */
  path: string;
  /** Path prefixes owned by this section (segment-boundary match). */
  match: string[];
  /** Client module that gates this section; undefined = always visible. */
  module?: ModuleId;
  adminOnly?: boolean;
  /** Page tabs shown by the shared PageHeader on this section's pages. */
  tabs: NavTab[];
  /** Show a "▾" quick-jump next to the section in the section row. */
  quickJump?: boolean;
  /** Phone menu chips for sections that have no page tabs (e.g. Orders: All / + Create). */
  chips?: NavTab[];
}

export interface NavGroup {
  id: GroupId;
  labelKey: string;
  /** Entry point for groups without sections (Dashboard). */
  path?: string;
}

export const NAV_GROUPS: NavGroup[] = [
  { id: 'dashboard', labelKey: 'modules.dashboard', path: '/dashboard' },
  { id: 'operations', labelKey: 'nav.operations' },
  { id: 'shipping', labelKey: 'modules.shipping' },
  { id: 'returns', labelKey: 'nav.returns' },
];

export const ADMIN_GROUP: NavGroup = { id: 'admin', labelKey: 'nav.admin', path: '/admin/users' };

export const NAV_SECTIONS: NavSection[] = [
  // Operations
  { id: 'orders', groupId: 'operations', labelKey: 'modules.orders', path: '/orders', match: ['/orders'], module: 'orders', tabs: [],
    chips: [
      { path: '/orders', labelKey: 'nav.allOrders', end: true },
      { path: '/orders/new', labelKey: 'nav.createOrderChip', end: true },
    ],
  },
  { id: 'products', groupId: 'operations', labelKey: 'modules.products', path: '/products', match: ['/products'], module: 'products', tabs: [] },
  { id: 'warehousing', groupId: 'operations', labelKey: 'modules.warehousing', path: '/warehousing', match: ['/warehousing'], module: 'warehousing', tabs: [] },
  { id: 'receiving', groupId: 'operations', labelKey: 'modules.receiving', path: '/reception', match: ['/reception'], module: 'receiving', tabs: [] },
  {
    id: 'procurement',
    groupId: 'operations',
    labelKey: 'modules.procurement',
    path: '/procurement/purchase-orders',
    match: ['/procurement'],
    module: 'procurement',
    quickJump: true,
    tabs: [
      { path: '/procurement/purchase-orders', labelKey: 'procurement.purchaseOrders' },
      { path: '/procurement/suppliers', labelKey: 'procurement.suppliers' },
    ],
  },
  {
    id: 'replenishment',
    groupId: 'operations',
    labelKey: 'modules.replenishment',
    path: '/replenishment/report',
    match: ['/replenishment'],
    module: 'replenishment',
    quickJump: true,
    tabs: [
      { path: '/replenishment/report', labelKey: 'replenishment.report' },
      { path: '/replenishment/products', labelKey: 'replenishment.supplierProducts' },
      { path: '/replenishment/groups', labelKey: 'replenishment.groups' },
    ],
  },
  // Shipping ("New shipment" is a button on Shipments, not a section)
  { id: 'shipments', groupId: 'shipping', labelKey: 'nav.shipments', path: '/shipments', match: ['/shipments', '/new-shipment'], module: 'shipping', tabs: [] },
  { id: 'addresses', groupId: 'shipping', labelKey: 'nav.addresses', path: '/addresses', match: ['/addresses'], module: 'shipping', tabs: [] },
  { id: 'shipping-report', groupId: 'shipping', labelKey: 'nav.shippingReport', path: '/reports', match: ['/reports'], module: 'shipping', tabs: [] },
  // Returns
  { id: 'rma', groupId: 'returns', labelKey: 'modules.rma', path: '/rma', match: ['/rma'], module: 'rma', tabs: [] },
  { id: 'claims', groupId: 'returns', labelKey: 'modules.claims', path: '/claims', match: ['/claims'], module: 'claims', tabs: [] },
  // Held returns waiting on the client's decision (Compass Retourneur). Shown to
  // fulfillment (orders) clients; the route itself is ungated so the decision
  // email's deep link always lands.
  { id: 'return-decisions', groupId: 'returns', labelKey: 'nav.returnDecisions', path: '/retours/decisions', match: ['/retours/decisions'], module: 'orders', tabs: [] },
  // Admin: its pages are the tabs
  {
    id: 'admin',
    groupId: 'admin',
    labelKey: 'nav.admin',
    path: '/admin/users',
    match: ['/admin'],
    adminOnly: true,
    tabs: [
      { path: '/admin/users', labelKey: 'nav.adminUsers' },
      { path: '/admin/clients', labelKey: 'nav.adminClients' },
      { path: '/admin/addresses', labelKey: 'nav.adminAddresses' },
      { path: '/admin/rma', labelKey: 'nav.adminRma' },
      { path: '/admin/claims', labelKey: 'nav.adminClaims' },
      { path: '/admin/carrier-contacts', labelKey: 'nav.adminCarrierContacts' },
    ],
  },
];

export interface VisibleNavGroup extends NavGroup {
  sections: NavSection[];
}

export interface VisibleNav {
  /** Dashboard + visible groups, in display order. */
  groups: VisibleNavGroup[];
  /** Admin group (null for non-admins). */
  admin: VisibleNavGroup | null;
}

/** Filter the manifest by enabled client modules and admin role. */
export function getVisibleNav(enabledModules: readonly string[], isAdmin: boolean): VisibleNav {
  const visibleSection = (s: NavSection) => {
    if (s.adminOnly) return isAdmin;
    return !s.module || enabledModules.includes(s.module);
  };
  const groups: VisibleNavGroup[] = NAV_GROUPS.map((g) => ({
    ...g,
    sections: NAV_SECTIONS.filter((s) => s.groupId === g.id && visibleSection(s)),
  })).filter((g) => g.id === 'dashboard' || g.sections.length > 0);

  const adminSections = NAV_SECTIONS.filter((s) => s.groupId === 'admin' && visibleSection(s));
  const admin = adminSections.length > 0 ? { ...ADMIN_GROUP, sections: adminSections } : null;
  return { groups, admin };
}

function prefixMatches(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(prefix + '/');
}

/** Section that owns a pathname, or null (profile, unknown). Dashboard has no section. */
export function matchSection(pathname: string): NavSection | null {
  for (const s of NAV_SECTIONS) {
    if (s.match.some((p) => prefixMatches(pathname, p))) return s;
  }
  return null;
}

/** Group a pathname belongs to ('dashboard' for / and /dashboard), or null. */
export function matchGroupId(pathname: string): GroupId | null {
  if (pathname === '/' || prefixMatches(pathname, '/dashboard')) return 'dashboard';
  return matchSection(pathname)?.groupId ?? null;
}

/** Is this tab the active one for pathname? */
export function isTabActive(tab: NavTab, pathname: string): boolean {
  return tab.end ? pathname === tab.path : prefixMatches(pathname, tab.path);
}

/* ── Last-used section per group (localStorage) ── */

const LAST_KEY = 'arivo.nav.lastSection';

function readLast(): Record<string, string> {
  try {
    const raw = window.localStorage.getItem(LAST_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

export function rememberSection(groupId: GroupId, sectionId: string): void {
  try {
    window.localStorage.setItem(LAST_KEY, JSON.stringify({ ...readLast(), [groupId]: sectionId }));
  } catch {
    /* storage unavailable: navigation still works, just without memory */
  }
}

/**
 * Where clicking a group goes: the last-used section if it is still visible,
 * otherwise the group's first visible section.
 */
export function resolveGroupPath(group: VisibleNavGroup): string {
  if (group.sections.length === 0) return group.path ?? '/dashboard';
  const last = readLast()[group.id];
  const section = group.sections.find((s) => s.id === last) ?? group.sections[0]!;
  return section.path;
}
